import React from 'react';
import { Link } from 'react-router-dom';
import {
    Facebook,
    Linkedin,
    Instagram,
    Mail,
    Phone,
    MapPin,
} from 'lucide-react';
import { PolygotsLogo } from './PolygotsLogo';

export const Footer = () => {
    return (
        <footer className="relative z-10 bg-slate-900 border-t border-slate-800 pt-12 pb-8">
            <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-4 mb-10">
                    {/* Brand */}
                    <div className="lg:col-span-1">
                        <Link to="/" className="inline-block mb-4">
                            <PolygotsLogo size="small" />
                        </Link>
                        <p className="text-slate-400 text-xs mb-6 leading-relaxed max-w-[200px]">
                            Connecting exceptional healthcare professionals with world-class
                            opportunities in Germany.
                        </p>
                        <div className="flex gap-2">
                            <a
                                href="https://www.facebook.com/share/17VJ4QX4E6/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-cyan-500 hover:text-white transition-all duration-300 hover:-translate-y-1"
                            >
                                <Facebook className="w-4 h-4" />
                            </a>
                            <a
                                href="https://www.linkedin.com/company/polyglots-talent-connect/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-cyan-500 hover:text-white transition-all duration-300 hover:-translate-y-1"
                            >
                                <Linkedin className="w-4 h-4" />
                            </a>
                            <a
                                href="https://www.instagram.com/newpolyglots_academy?igsh=MTJxcDBpeWFjYXdvMw=="
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-cyan-500 hover:text-white transition-all duration-300 hover:-translate-y-1"
                            >
                                <Instagram className="w-4 h-4" />
                            </a>
                            <a
                                href="https://wa.me/917012702384"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-cyan-500 hover:text-white transition-all duration-300 hover:-translate-y-1"
                            >
                                <Phone className="w-4 h-4" />
                            </a>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="text-white font-bold mb-5 text-sm">Quick Links</h3>
                        <ul className="space-y-3">
                            {[
                                { name: 'For Talents', href: '/login?role=candidate' },
                                { name: 'For Employers', href: '/login?role=recruiting_partner' },
                                { name: 'Success Stories', href: '/#testimonials' },
                                { name: 'About Us', href: '/about' },
                            ].map((item) => (
                                <li key={item.name}>
                                    <Link
                                        to={item.href}
                                        className="text-slate-400 hover:text-cyan-400 transition-colors text-xs"
                                    >
                                        {item.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Careers */}
                    <div>
                        <h3 className="text-white font-bold mb-5 text-sm">Careers</h3>
                        <ul className="space-y-3">
                            {[
                                { name: 'Healthcare', href: '/careers/healthcare' },
                                { name: 'Technical & Industrial', href: '/careers/technical-industrial' },
                                { name: 'Logistics & Transport', href: '/careers/logistics-transport' },
                                { name: 'Vocational Programs', href: '/careers/vocational-programs' },
                            ].map((item) => (
                                <li key={item.name}>
                                    <Link
                                        to={item.href}
                                        className="text-slate-400 hover:text-cyan-400 transition-colors text-xs"
                                    >
                                        {item.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Legal */}
                    <div>
                        <h3 className="text-white font-bold mb-5 text-sm">Legal</h3>
                        <ul className="space-y-3">
                            {[
                                { name: 'Privacy Policy', href: '/privacy' },
                                { name: 'Terms of Service', href: '/terms' },
                                { name: 'Cookie Policy', href: '/cookies' },
                                { name: 'Imprint', href: '/imprint' },
                                { name: 'Data Protection', href: '/data-protection' },
                            ].map((item) => (
                                <li key={item.name}>
                                    <Link
                                        to={item.href}
                                        className="text-slate-400 hover:text-cyan-400 transition-colors text-xs"
                                    >
                                        {item.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Us */}
                    <div className="lg:col-span-1">
                        <h3 className="text-white font-bold mb-5 text-sm">Contact Us</h3>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-2 text-slate-400">
                                <MapPin className="w-4 h-4 text-cyan-500 shrink-0 mt-0.5" />
                                <span className="text-xs leading-relaxed">
                                    Marzahner Ch 199, Berlin 12681, Germany
                                </span>
                            </li>
                            <li className="flex items-center gap-2 text-slate-400">
                                <Phone className="w-4 h-4 text-cyan-500 shrink-0" />
                                <span className="text-xs">+49 176 59581516 / +91 8089713733</span>
                            </li>
                            <li className="flex items-center gap-2 text-slate-400">
                                <Mail className="w-4 h-4 text-cyan-500 shrink-0" />
                                <span className="text-xs truncate">info@polyglotstalent.com</span>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="border-t border-slate-800 mt-8 pt-6 text-center text-slate-500 text-[10px]">
                    <p>
                        &copy; {new Date().getFullYear()} Polyglots GmbH. All rights
                        reserved.
                    </p>
                </div>
            </div>
        </footer>
    );
};
